import {
  ErrorTypes,
  makeValidationErrors,
  type ValidationError,
  type ValidationResult,
} from './errors';
import {
  type FieldsField,
  type Form,
  type Validation,
  ValidationFormat,
} from '@wix/ambassador-forms-v4-form/types';
import { deriveError } from '../error-derivation';

import { createAjvSchemaValidatorFactory } from './create-ajv-schema-validator';
import { formSchemaMapperFactory } from './mappers/form-schema-mapper';
import { propertySchemaMapper } from './mappers/property-schema-mapper';
import { type Submission } from '../../submission';
import {
  type FormField,
  ValidationMode,
} from '@wix/ambassador-forms-v1-validation-result/types';
import { deepOmitEmptyData } from '../utils/deep-omit-empty-data';
import type Ajv from 'ajv';
import { transformPlatformisedFieldToValidation } from './mappers/platformised-fields/transform-platformised-field-to-validation';
import { formSchemaMapperFactoryPlatformised } from './mappers/platformised-fields/form-schema-mapper-platformised';

type AddressFormProvider = (
  field: FormField,
  submissionValue: unknown,
) => Form | undefined;

export class FormValidator {
  constructor(
    private readonly formSchemas: Form[],
    private readonly validationMode?: ValidationMode,
    private readonly platformisedFieldBasedValidation: Boolean = false,
    private readonly addressFormProvider: AddressFormProvider = () => undefined,
    private readonly submissionValueProvider?: (
      submission: Submission,
      form: Form,
    ) => Submission,
    private readonly libraryBasedPhoneFieldValidationEnabled: boolean = false,
    private readonly lenientPhoneValidation: boolean = false,
  ) {
    if (!this.formSchemas.length) {
      throw new Error('At least one form schema is required for validation');
    }
  }

  validateFields(
    formId: string,
    _submission: Submission,
    fieldIdMask?: string | string[],
  ) {
    const fieldMasks = [fieldIdMask ?? []].flat();
    const { ajv, submission, currentlyValidatableForm } =
      this.prepareValidation(formId, _submission);
    const normalizedSubmission = deepOmitEmptyData(submission);

    if (fieldMasks.length) {
      const errors = this.validateSubmissionWithFieldMask(
        ajv,
        formId,
        normalizedSubmission,
        fieldMasks,
      );

      return { errors, currentlyValidatableForm };
    }

    return {
      errors: this.validateWholeSubmission(
        ajv,
        currentlyValidatableForm.id!,
        normalizedSubmission,
      ),
      currentlyValidatableForm,
    };
  }

  validate(
    formId: string,
    _submission: Submission,
    fieldIdMask?: string | string[],
  ): ValidationResult {
    const fieldMasks = [fieldIdMask ?? []].flat();

    const { errors, currentlyValidatableForm } = this.validateFields(
      formId,
      _submission,
      fieldMasks,
    );

    if (errors.length > 0) {
      return { errors };
    }

    if (!fieldMasks.length) {
      const canUpdateForm = this.validateCanUpdateFormNotDisabled(
        currentlyValidatableForm,
      );

      if (canUpdateForm.errors) {
        return canUpdateForm;
      }
    }

    return { errors: [] };
  }

  private prepareValidation(formId: string, _submission: Submission) {
    const currentlyValidatableForm = this.formSchemas.find(
      (form) => form.id === formId,
    );

    if (!currentlyValidatableForm) {
      throw new Error(`Missing form ${formId} for validation`);
    }

    const submission = this.submissionValueProvider
      ? this.submissionValueProvider(_submission, currentlyValidatableForm)
      : _submission;

    const ajv = this.initAJvWithSchemas(currentlyValidatableForm, submission);

    return { ajv, submission, currentlyValidatableForm };
  }

  private getAddressForms(formSchemas: Form, submission: Submission): Form[] {
    const addressFields = [
      ...(formSchemas.fields ?? []),
      ...(formSchemas.deletedFields ?? []),
    ].filter(
      (field) =>
        field.validation?.predefined?.format ===
        ValidationFormat.MULTILINE_ADDRESS,
    );

    return addressFields.reduce((acc, field) => {
      const value = submission[field.target!];
      const addressForm = this.addressFormProvider(field, value);

      if (addressForm) {
        acc.push({ ...addressForm, id: field.target! } as Form);
      }

      return acc;
    }, [] as Form[]);
  }

  private getObjectArrayForms(formSchemas: Form): Form[] {
    const objectArrayFields = [
      ...(formSchemas.fields ?? []),
      ...(formSchemas.deletedFields ?? []),
    ].filter(
      (field) =>
        field.validation?.predefined?.format === ValidationFormat.OBJECT_ARRAY,
    );

    return objectArrayFields.map((field) => {
      return { id: field.target, fields: field.view?.fields ?? [] };
    });
  }

  private validateCanUpdateFormNotDisabled(form: Form) {
    if (
      this.validationMode !== ValidationMode.UNRESTRICTED_UPDATE &&
      this.isFormDisabled(form)
    ) {
      return {
        errors: [
          {
            errorPath: 'form.properties.disabled',
            errorType: ErrorTypes.DISABLED_FORM_ERROR,
            errorMessage: 'Form is disabled and not accepting submissions',
          },
        ],
      };
    } else {
      return {};
    }
  }

  private validateWholeSubmission(
    ajv: Ajv,
    formId: string,
    fieldSubmission: Submission,
  ): ValidationError[] {
    const validate = ajv.getSchema(`${formId}`);
    if (!validate) {
      throw new Error(`Missing precompiled schema for form ${formId}`);
    }
    validate(fieldSubmission);
    const baseErrors = makeValidationErrors(validate.errors);

    return this.deriveErrors(formId, baseErrors, fieldSubmission);
  }

  private validateSubmissionWithFieldMask(
    ajv: Ajv,
    formId: string,
    fieldSubmission: Submission,
    fieldIdMasks: string[],
  ) {
    const baseErrors = fieldIdMasks.reduce<ValidationError[]>((acc, mask) => {
      const validate = ajv.getSchema(`${formId}#/properties/${mask}`);
      if (!validate) {
        acc.push({
          errorPath: `${mask}`,
          errorType: ErrorTypes.UNKNOWN_VALUE_ERROR,
          errorMessage: `${mask} property not expected in schema`,
        });
        return acc;
      }

      const valid = validate(fieldSubmission[mask]);

      if (!valid) {
        acc.push(...makeValidationErrors(validate.errors, mask));
      }
      return acc;
    }, []);

    return this.deriveErrors(formId, baseErrors, fieldSubmission);
  }

  private deriveErrors(
    formId: string,
    errors: ValidationError[],
    submission: Submission,
  ): ValidationError[] {
    return errors
      .map((error) => {
        const validation = this.getFieldValidation(formId, error.errorPath);
        const value = this.getFieldValue(submission, error.errorPath);

        return deriveError(validation, error, { value });
      })
      .filter((error): error is ValidationError => error !== undefined);
  }

  private getFieldValidation(
    formId: string,
    fieldPath: string,
  ): Validation | undefined {
    const form = this.formSchemas.find((f) => f.id === formId);
    if (!form) {
      return undefined;
    }

    const pathParts = fieldPath.split('/');
    const rootFieldId = pathParts[0];

    const platformisedField = [
      ...(form.formFields ?? []),
      ...(form.deletedFormFields ?? []),
    ].find((f) => {
      if (f.id === rootFieldId) {
        return true;
      }

      const inputTarget = f.inputOptions?.target;

      if (inputTarget === rootFieldId) {
        return true;
      }

      return false;
    });

    const legacyField = [
      ...(form.fields ?? []),
      ...(form.deletedFields ?? []),
    ].find((f) => {
      if (f.id === rootFieldId || f.target === rootFieldId) {
        return true;
      }

      return false;
    });

    if (platformisedField) {
      return this.getPlatformisedFieldValidation(platformisedField);
    } else if (legacyField) {
      return this.getLegacyFieldValidation(legacyField);
    } else {
      return undefined;
    }
  }

  private getLegacyFieldValidation(field: FormField): Validation | undefined {
    return field.validation;
  }

  private getPlatformisedFieldValidation(
    field: FieldsField,
  ): Validation | undefined {
    const inputOptions = field.inputOptions;
    if (!inputOptions) {
      return undefined;
    }

    const validation: Partial<Validation> = {};

    if (inputOptions.numberOptions?.validation) {
      validation.number = inputOptions.numberOptions.validation;
    }

    // platformised fields are not type-compatible with the Validation interface.
    // however, deriveError only accesses common properties like minLength, maxLength, etc.
    if (inputOptions.stringOptions?.validation) {
      validation.string = inputOptions.stringOptions
        .validation as unknown as Validation['string'];
    }

    if (inputOptions.arrayOptions?.validation) {
      validation.array = inputOptions.arrayOptions
        .validation as unknown as Validation['array'];
    }

    if (Object.keys(validation).length > 0) {
      return validation as Validation;
    }

    return undefined;
  }

  private getFieldValue(
    submission: Submission,
    fieldPath: string,
  ): string | undefined {
    const pathParts = fieldPath.split('/');
    let value: any = submission;

    for (const part of pathParts) {
      value = value?.[part];
    }

    return typeof value === 'string' ? value : undefined;
  }

  private isFormDisabled(formSchema: Form): boolean {
    const isDeadlinePast =
      formSchema?.limitationRule?.dateTimeDeadline &&
      new Date(formSchema.limitationRule.dateTimeDeadline).getTime() <=
        new Date().getTime();
    return Boolean(formSchema?.properties?.disabled || isDeadlinePast);
  }

  private getFormSchemaMapper() {
    return this.platformisedFieldBasedValidation
      ? formSchemaMapperFactoryPlatformised(
          transformPlatformisedFieldToValidation,
          this.validationMode ?? ValidationMode.UNKNOWN,
          this.libraryBasedPhoneFieldValidationEnabled,
        )
      : formSchemaMapperFactory(
          propertySchemaMapper,
          this.validationMode ?? ValidationMode.UNKNOWN,
          this.libraryBasedPhoneFieldValidationEnabled,
        );
  }

  private initAJvWithSchemas(
    currentlyValidatableForm: Form,
    submission: Submission,
  ) {
    const formSchemaMapper = this.getFormSchemaMapper();
    const createAjvSchemaValidator = createAjvSchemaValidatorFactory(
      formSchemaMapper,
      this.libraryBasedPhoneFieldValidationEnabled &&
        this.validationMode !== ValidationMode.UNRESTRICTED_UPDATE,
      this.lenientPhoneValidation,
    );

    const nestedForms = this.formSchemas.flatMap((schema) =>
      (schema.nestedForms || []).flatMap(({ targets, form }) =>
        (targets || []).map((target) => ({ ...form, id: target })),
      ),
    );

    const addressForms = this.getAddressForms(
      currentlyValidatableForm,
      submission,
    );

    const objectArrayForms = this.getObjectArrayForms(currentlyValidatableForm);

    return createAjvSchemaValidator([
      ...this.formSchemas,
      ...nestedForms,
      ...addressForms,
      ...objectArrayForms,
    ]);
  }
}
